// Kalender (Dagsedler) — Outlook-grad: måned/uke/dag-visning, opprett/rediger/
// slett hendelser, deltakere + RSVP, påminnelser, gjentakelse og «inviter til
// GNÅ-møte». Echelon-distribuert (kaskade) og delt over hubben (collab 'calendar').
// Ren DOM (felt-tekst via textContent). Logikk fra @skade/contracts (calendar.ts).
import {
  activitiesInRange, activitiesForUnit, ACTIVITY_TYPE_LABEL, ACTIVITY_TYPE_COLOR, ACTIVITY_STATUS_LABEL,
  ActivityType, canEditActivity, CalendarActivitySchema, rsvpSummary, type ActivityInstance,
  type CalendarActivity, type UnitRef, type ActivityStatus, type Rsvp,
} from '@skade/contracts';

export interface CalendarHooks {
  getActivities: () => CalendarActivity[];
  getSelfUnit: () => UnitRef;
  getSelfId: () => string;
  onSave: (a: CalendarActivity) => void;
  onFocus?: (a: CalendarActivity) => void;
  /** «Inviter til GNÅ-møte» — åpne/knytt et samvirkerom (Feltveksler). */
  onMeeting?: (a: CalendarActivity) => void;
}

type View = 'måned' | 'uke' | 'dag';
const DAY = 86_400_000;
const WD = ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'];
const MON = ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember'];
const RSVP_LABEL: Record<Rsvp, string> = { invitert: 'Invitert', godtatt: 'Godtatt', avslått: 'Avslått', tentativ: 'Tentativ' };
const STATUS_FLOW: ActivityStatus[] = ['forslag', 'planlagt', 'beordret', 'pågår', 'fullført'];

// ── Dato-hjelpere (lokal tid; lagres som epoch ms) ──────────────────────────
function startOfDay(ms: number): number { const d = new Date(ms); d.setHours(0, 0, 0, 0); return d.getTime(); }
function startOfWeek(ms: number): number { const d = new Date(startOfDay(ms)); const wd = (d.getDay() + 6) % 7; return d.getTime() - wd * DAY; } // mandag
function startOfMonth(ms: number): number { const d = new Date(ms); d.setDate(1); d.setHours(0, 0, 0, 0); return d.getTime(); }
function addMonths(ms: number, n: number): number { const d = new Date(ms); d.setMonth(d.getMonth() + n); return d.getTime(); }
function sameDay(a: number, b: number): boolean { return startOfDay(a) === startOfDay(b); }
function hhmm(ms: number): string { const d = new Date(ms); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; }
function toLocalInput(ms: number): string { const d = new Date(ms - new Date(ms).getTimezoneOffset() * 60_000); return d.toISOString().slice(0, 16); }
function fromLocalInput(v: string): number { return v ? new Date(v).getTime() : Date.now(); }

export class CalendarPanel {
  readonly el: HTMLElement;
  private open = false;
  private view: View = 'måned';
  private cursor = Date.now();
  private onlyMine = false;
  private bodyEl!: HTMLElement;
  private titleEl!: HTMLElement;
  private editing: CalendarActivity | null = null;

  constructor(private hooks: CalendarHooks) {
    this.el = this.build();
    this.render();
  }

  toggle() { this.open = !this.open; this.el.classList.toggle('open', this.open); if (this.open) this.render(); }

  private build(): HTMLElement {
    const p = div('report-composer calendar-panel');
    const head = div('report-head');
    head.append(span('report-title', 'Kalender — Dagsedler'));
    const x = document.createElement('button');
    x.className = 'report-x'; x.textContent = '×'; x.onclick = () => this.toggle();
    head.append(x);
    p.append(head);

    const nav = div('cal-nav');
    nav.append(
      navBtn('‹', () => this.step(-1)),
      navBtn('I dag', () => { this.cursor = Date.now(); this.render(); }, 'cal-today'),
      navBtn('›', () => this.step(1)),
    );
    this.titleEl = div('cal-title'); nav.append(this.titleEl);
    const views = div('cal-views');
    for (const v of ['måned', 'uke', 'dag'] as View[]) {
      const b = navBtn(v[0].toUpperCase() + v.slice(1), () => { this.view = v; this.render(); }, 'cal-viewbtn');
      b.dataset.view = v; views.append(b);
    }
    nav.append(views);
    nav.append(navBtn('+ Ny hendelse', () => this.openEditor(null), 'cal-add'));
    p.append(nav);

    const filt = div('cal-filter');
    const mine = document.createElement('label'); mine.className = 'cal-mine';
    const cb = document.createElement('input'); cb.type = 'checkbox'; cb.checked = this.onlyMine;
    cb.onchange = () => { this.onlyMine = cb.checked; this.render(); };
    mine.append(cb, document.createTextNode(' Bare mine/min enhet'));
    filt.append(mine);
    p.append(filt);

    this.bodyEl = div('cal-body');
    p.append(this.bodyEl);
    return p;
  }

  private step(dir: number) {
    if (this.view === 'måned') this.cursor = addMonths(this.cursor, dir);
    else if (this.view === 'uke') this.cursor += dir * 7 * DAY;
    else this.cursor += dir * DAY;
    this.render();
  }

  private instances(startMs: number, endMs: number): ActivityInstance[] {
    let acts = this.hooks.getActivities();
    if (this.onlyMine) {
      const me = this.hooks.getSelfUnit();
      const selfId = this.hooks.getSelfId();
      const mine = new Set(activitiesForUnit(acts, me).map((a) => a.id));
      acts = acts.filter((a) => mine.has(a.id) || a.attendees.some((p) => p.id === selfId));
    }
    return activitiesInRange(acts, startMs, endMs);
  }

  render() {
    if (!this.open) return;
    for (const b of this.el.querySelectorAll<HTMLElement>('.cal-viewbtn')) b.classList.toggle('active', b.dataset.view === this.view);
    this.bodyEl.replaceChildren();
    if (this.view === 'måned') this.renderMonth();
    else if (this.view === 'uke') this.renderWeek();
    else this.renderDay();
  }

  private renderMonth() {
    const first = startOfMonth(this.cursor);
    const gridStart = startOfWeek(first);
    const d = new Date(first);
    this.titleEl.textContent = `${MON[d.getMonth()]} ${d.getFullYear()}`;
    const inst = this.instances(gridStart, gridStart + 42 * DAY);
    const byDay = new Map<number, ActivityInstance[]>();
    for (const i of inst) { const k = startOfDay(i.startMs); const arr = byDay.get(k) ?? []; arr.push(i); byDay.set(k, arr); }

    const grid = div('cal-month');
    for (const w of WD) grid.append(span('cal-wd', w));
    for (let n = 0; n < 42; n++) {
      const dayMs = gridStart + n * DAY;
      const cell = div('cal-cell');
      if (new Date(dayMs).getMonth() !== d.getMonth()) cell.classList.add('cal-oob');
      if (sameDay(dayMs, Date.now())) cell.classList.add('cal-todaycell');
      const num = span('cal-daynum', String(new Date(dayMs).getDate()));
      cell.append(num);
      cell.onclick = (e) => { if (e.target === cell || e.target === num) this.openEditor(null, dayMs); };
      const items = (byDay.get(dayMs) ?? []).sort((a, b) => a.startMs - b.startMs);
      for (const i of items.slice(0, 3)) cell.append(this.chip(i));
      if (items.length > 3) cell.append(span('cal-more', `+${items.length - 3} mer`));
      grid.append(cell);
    }
    this.bodyEl.append(grid);
  }

  private renderWeek() {
    const ws = startOfWeek(this.cursor);
    const a = new Date(ws), b = new Date(ws + 6 * DAY);
    this.titleEl.textContent = `Uke — ${a.getDate()}.–${b.getDate()}. ${MON[b.getMonth()]}`;
    const inst = this.instances(ws, ws + 7 * DAY);
    const wrap = div('cal-week');
    for (let n = 0; n < 7; n++) {
      const dayMs = ws + n * DAY;
      const col = div('cal-wcol');
      if (sameDay(dayMs, Date.now())) col.classList.add('cal-todaycell');
      const h = div('cal-wcolhead');
      h.append(span('cal-wd', WD[n]), span('cal-daynum', String(new Date(dayMs).getDate())));
      h.onclick = () => this.openEditor(null, dayMs);
      col.append(h);
      const items = inst.filter((i) => sameDay(i.startMs, dayMs)).sort((x, y) => x.startMs - y.startMs);
      if (!items.length) col.append(span('cal-empty-sm', '—'));
      for (const i of items) col.append(this.chip(i, true));
      wrap.append(col);
    }
    this.bodyEl.append(wrap);
  }

  private renderDay() {
    const ds = startOfDay(this.cursor);
    const d = new Date(ds);
    this.titleEl.textContent = `${WD[(d.getDay() + 6) % 7]} ${d.getDate()}. ${MON[d.getMonth()]} ${d.getFullYear()}`;
    const items = this.instances(ds, ds + DAY).sort((a, b) => a.startMs - b.startMs);
    const wrap = div('cal-day');
    if (!items.length) { wrap.append(this.emptyState('Ingen hendelser denne dagen', 'Klikk «+ Ny hendelse» for å planlegge.')); this.bodyEl.append(wrap); return; }
    for (const i of items) {
      const rowEl = div('cal-dayrow');
      rowEl.append(span('cal-daytime', i.activity.allDay ? 'Hele dagen' : `${hhmm(i.startMs)}–${hhmm(i.endMs)}`), this.chip(i, true, true));
      wrap.append(rowEl);
    }
    this.bodyEl.append(wrap);
  }

  private chip(i: ActivityInstance, withTime = false, big = false): HTMLElement {
    const a = i.activity;
    const c = div('cal-chip' + (big ? ' big' : ''));
    c.style.borderLeftColor = ACTIVITY_TYPE_COLOR[a.type] ?? '#8b9b8b';
    const label = (withTime && !a.allDay ? `${hhmm(i.startMs)} ` : '') + a.title;
    c.append(span('cal-chiptxt', label + (i.recurring ? ' ↻' : '')));
    if (big) c.append(span('cal-chipmeta', `${ACTIVITY_TYPE_LABEL[a.type]} · ${ACTIVITY_STATUS_LABEL[a.status]}${a.attendees.length ? ` · ${a.attendees.length} delt.` : ''}${a.meeting ? ' · 📡 GNÅ-møte' : ''}`));
    c.onclick = (e) => { e.stopPropagation(); this.openEditor(a); };
    return c;
  }

  private emptyState(title: string, sub: string): HTMLElement {
    const e = div('cal-emptystate');
    e.append(span('cal-empty-ico', '▦'), span('cal-empty-t', title), span('cal-empty-s', sub));
    return e;
  }

  // ── Editor ──────────────────────────────────────────────────────────────
  private openEditor(a: CalendarActivity | null, defaultDayMs?: number) {
    const now = defaultDayMs ? defaultDayMs + 9 * 3_600_000 : Date.now();
    // Utkast bygges som literal (tom tittel tillatt under redigering); valideres
    // først ved lagring via CalendarActivitySchema.parse.
    this.editing = a ?? {
      id: `act-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`,
      title: '', type: 'annet', status: 'forslag', startMs: now, endMs: now + 3_600_000,
      allDay: false, recurrence: 'ingen', issuedBy: this.hooks.getSelfUnit(), assignedTo: [],
      createdBy: this.hooks.getSelfId(), classification: 'UGRADERT', attendees: [], tsMs: Date.now(),
    } as CalendarActivity;
    this.renderEditor();
  }

  private renderEditor() {
    const a = this.editing!;
    const canEdit = canEditActivity(this.hooks.getSelfUnit().level, a);
    const ov = div('cal-editor-ov');
    const box = div('cal-editor');
    const head = div('cal-editor-head');
    head.append(span('cal-editor-t', a.title ? 'Rediger hendelse' : 'Ny hendelse'));
    const close = document.createElement('button'); close.className = 'report-x'; close.textContent = '×';
    close.onclick = () => { this.editing = null; ov.remove(); };
    head.append(close);
    box.append(head);

    const form = div('cal-form');
    const patch: Partial<CalendarActivity> = {};
    const set = <K extends keyof CalendarActivity>(k: K, v: CalendarActivity[K]) => { (patch as Record<string, unknown>)[k as string] = v; };

    const title = textInput(a.title, 'Tittel', (v) => set('title', v)); title.disabled = !canEdit;
    form.append(field('Tittel', title));

    const type = select(ActivityType.options, a.type, ACTIVITY_TYPE_LABEL, (v) => set('type', v as CalendarActivity['type']));
    const status = select(STATUS_FLOW, a.status, ACTIVITY_STATUS_LABEL, (v) => set('status', v as ActivityStatus));
    form.append(row2(field('Type', type), field('Status', status)));

    form.append(row2(field('Start', dtInput(a.startMs, (v) => set('startMs', v))), field('Slutt', dtInput(a.endMs, (v) => set('endMs', v)))));

    const rec = select(['ingen', 'daglig', 'ukentlig', 'månedlig', 'årlig'] as const, a.recurrence, {
      ingen: 'Ingen', daglig: 'Daglig', ukentlig: 'Ukentlig', 'månedlig': 'Månedlig', 'årlig': 'Årlig',
    }, (v) => set('recurrence', v as CalendarActivity['recurrence']));
    const remind = select(['', '0', '10', '30', '60', '1440'] as const, String(a.reminderMinutes ?? ''), {
      '': 'Ingen påminnelse', '0': 'Ved start', '10': '10 min før', '30': '30 min før', '60': '1 t før', '1440': '1 dag før',
    }, (v) => set('reminderMinutes', v === '' ? undefined : Number(v)));
    form.append(row2(field('Gjentakelse', rec), field('Påminnelse', remind)));

    const desc = document.createElement('textarea'); desc.className = 'cal-in cal-ta'; desc.value = a.description ?? '';
    desc.placeholder = 'Beskrivelse / oppdragstekst'; desc.oninput = () => set('description', desc.value);
    form.append(field('Beskrivelse', desc));

    // Deltakere + RSVP
    const att = div('cal-att');
    const renderAtt = () => {
      att.replaceChildren();
      const cur = patch.attendees ?? a.attendees;
      if (!cur.length) att.append(span('cal-empty-sm', 'Ingen deltakere ennå.'));
      for (const p of cur) {
        const chip = div('cal-attchip');
        chip.append(span('cal-attname', p.name));
        const rs = select(['invitert', 'godtatt', 'avslått', 'tentativ'] as const, p.rsvp, RSVP_LABEL, (v) => {
          set('attendees', (patch.attendees ?? a.attendees).map((x) => x.id === p.id ? { ...x, rsvp: v as Rsvp } : x));
        });
        rs.classList.add('cal-rsvp', `rsvp-${p.rsvp}`);
        chip.append(rs, navBtn('×', () => { set('attendees', (patch.attendees ?? a.attendees).filter((x) => x.id !== p.id)); renderAtt(); }, 'cal-attrm'));
        att.append(chip);
      }
    };
    renderAtt();
    const nameIn = textInput('', 'Navn/enhet', () => {});
    const addBtn = navBtn('+ Legg til', () => {
      const nm = nameIn.value.trim(); if (!nm) return;
      const id = `p-${nm.toLowerCase().replace(/\s+/g, '-')}`;
      const cur = patch.attendees ?? a.attendees;
      if (!cur.some((x) => x.id === id)) set('attendees', [...cur, { id, name: nm, rsvp: 'invitert' as Rsvp }]);
      nameIn.value = ''; renderAtt();
    }, 'cal-add');
    const addAtt = div('cal-addatt'); addAtt.append(nameIn, addBtn);
    form.append(field('Deltakere', wrapCol(att, addAtt)));

    // GNÅ-møte
    const meetWrap = div('cal-meet');
    const meetCb = document.createElement('input'); meetCb.type = 'checkbox'; meetCb.checked = !!a.meeting;
    const meetLbl = document.createElement('label'); meetLbl.className = 'cal-mine';
    meetLbl.append(meetCb, document.createTextNode(' 📡 GNÅ-møte (opprett samvirkerom)'));
    meetCb.onchange = () => set('meeting', meetCb.checked ? { kind: 'gna' as const, room: (patch.title ?? a.title) || 'Møte' } : undefined);
    meetWrap.append(meetLbl);
    form.append(meetWrap);
    box.append(form);

    // Handlinger
    const err = span('cal-err', '');
    const actions = div('cal-actions');
    if (canEdit) {
      actions.append(navBtn('Lagre', () => {
        const merged = CalendarActivitySchema.safeParse({ ...a, ...patch, tsMs: Date.now() });
        if (!merged.success) { err.textContent = 'Tittel og gyldig start/slutt kreves.'; return; }
        this.hooks.onSave(merged.data);
        if (merged.data.meeting && this.hooks.onMeeting) this.hooks.onMeeting(merged.data);
        this.editing = null; ov.remove(); this.render();
      }, 'cal-add'));
      const si = STATUS_FLOW.indexOf(a.status);
      if (si >= 0 && si < STATUS_FLOW.length - 1) {
        actions.append(navBtn(`→ ${ACTIVITY_STATUS_LABEL[STATUS_FLOW[si + 1]]}`, () => {
          this.hooks.onSave(CalendarActivitySchema.parse({ ...a, ...patch, status: STATUS_FLOW[si + 1], tsMs: Date.now() }));
          this.editing = null; ov.remove(); this.render();
        }, 'cal-viewbtn'));
      }
    } else {
      actions.append(span('cal-locked', 'Beordret ovenfra — låst (kan detaljeres som underoppdrag).'));
    }
    if (a.location && this.hooks.onFocus) actions.append(navBtn('Vis på kart', () => this.hooks.onFocus!(a), 'cal-viewbtn'));
    if (a.meeting && this.hooks.onMeeting) actions.append(navBtn('📡 Åpne GNÅ-møte', () => this.hooks.onMeeting!(a), 'cal-viewbtn'));
    box.append(actions, err);

    const sum = rsvpSummary(a);
    if (a.attendees.length) box.append(span('cal-rsvpsum', `Svar: ${sum.godtatt} godtatt · ${sum.tentativ} tentativ · ${sum.avslått} avslått · ${sum.invitert} ikke svart`));

    ov.append(box);
    ov.onclick = (e) => { if (e.target === ov) { this.editing = null; ov.remove(); } };
    this.el.append(ov);
  }
}

// ── DOM-hjelpere ────────────────────────────────────────────────────────────
function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
function navBtn(label: string, on: () => void, cls = 'cal-navbtn'): HTMLButtonElement {
  const b = document.createElement('button'); b.type = 'button'; b.className = cls; b.textContent = label;
  b.onclick = (e) => { e.stopPropagation(); on(); }; return b;
}
function field(label: string, ctrl: HTMLElement): HTMLElement { const w = div('cal-field'); w.append(span('cal-field-l', label), ctrl); return w; }
function row2(a: HTMLElement, b: HTMLElement): HTMLElement { const r = div('cal-row2'); r.append(a, b); return r; }
function wrapCol(...els: HTMLElement[]): HTMLElement { const w = div('cal-col'); w.append(...els); return w; }
function textInput(val: string, ph: string, on: (v: string) => void): HTMLInputElement {
  const i = document.createElement('input'); i.className = 'cal-in'; i.value = val; i.placeholder = ph; i.oninput = () => on(i.value); return i;
}
function dtInput(ms: number, on: (ms: number) => void): HTMLInputElement {
  const i = document.createElement('input'); i.type = 'datetime-local'; i.className = 'cal-in';
  i.value = toLocalInput(ms); i.oninput = () => on(fromLocalInput(i.value)); return i;
}
function select<T extends string>(opts: readonly T[], val: T, labels: Record<string, string>, on: (v: T) => void): HTMLSelectElement {
  const s = document.createElement('select'); s.className = 'cal-in';
  for (const o of opts) { const op = document.createElement('option'); op.value = o; op.textContent = labels[o] ?? o; if (o === val) op.selected = true; s.append(op); }
  s.onchange = () => on(s.value as T); return s;
}
